import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import navigation from "../../constant/layout/navigation";
import sectionService from "../../services/Sections/Home/sectionService";
import EmblemOfIndia from "../../assets/images/layout/Emblem_Of_India_White.png";
import SealOfMaharashtra from "../../assets/images/layout/Seal_of_Maharashtra.png";
import { changeLanguage } from "../../redux/slices/languageSlice";

import { Logout as LogoutIcon } from "@mui/icons-material";
import { useAuthActions } from "../../hooks/useAuth";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout } = useAuthActions();

  const selectedLanguage = useSelector(
    (state) => state.language.selectedLanguage || "मराठी"
  );

  const [sections, setSections] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [subjectsAnchor, setSubjectsAnchor] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const timerRef = useRef(null);

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    if (!selectedLanguage) {
      dispatch(changeLanguage("mr"));
    }
    i18n.changeLanguage(selectedLanguage || "mr");
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSections = async () => {
    try {
      const sectionsData = await sectionService.fetchSectionCardsWithLanguage(
        selectedLanguage
      );
      console.log("Sections Data:", sectionsData);
      const updatedSections = [
        { id: "home", section: "Home" },
        ...sectionsData,
      ];
      setSections(updatedSections);
      setIsInitialized(true);
      setError(null);
    } catch (err) {
      console.error("Error fetching sections:", err);
      setError("Failed to load sections");
      setTimeout(() => {
        if (!isInitialized) {
          fetchSections();
        }
      }, 2000);
    }
  };

  useEffect(() => {
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const handleLanguageChange = (languageCode) => {
    dispatch(changeLanguage(languageCode));
    i18n.changeLanguage(languageCode);
    setLanguageMenuAnchor(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const getLanguageLabel = (lang) => {
    if (lang === "en") return "English";
    if (lang === "hi") return "हिंदी";
    if (lang === "gu") return "ગુજરાતી";
    return "मराठी";
  };

  const handleSectionClick = (path) => {
    navigate(path);
  };

  const handleSubjectsMouseEnter = (event) => {
    setIsHovering(true);
    setSubjectsAnchor(event.currentTarget);
  };

  const handleSubjectsMouseLeave = () => {
    setIsHovering(false);
    setSubjectsAnchor(null);
  };

  useEffect(() => {
    if (!isHovering) {
      timerRef.current = setTimeout(() => {
        setSubjectsAnchor(null);
      }, 100);
    } else if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [isHovering]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallMobile = useMediaQuery("(max-width: 480px)");

  return (
    <div id="navbar">
      <AppBar position="static" sx={{ backgroundColor: "#061c36" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: { xs: "8px 12px", sm: "8px 16px", md: "8px 24px" },
            minHeight: { xs: "56px", sm: "64px" },
          }}
        >
          {/* Left Section - Logos */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => navigate("/")}
          >
            <img
              src={EmblemOfIndia}
              alt="Emblem of India"
              style={{
                width: isMobile ? 35 : isTablet ? 40 : 50,
                height: isMobile ? 35 : isTablet ? 40 : 50,
              }}
            />
            <img
              src={SealOfMaharashtra}
              alt="Seal of Maharashtra"
              style={{
                width: isMobile ? 35 : isTablet ? 40 : 50,
                height: isMobile ? 35 : isTablet ? 40 : 50,
              }}
            />
            {!isMobile && !isTablet && (
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { md: "1.25rem", lg: "1.5rem" },
                  }}
                >
                  General Administration Department
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontSize: { md: "0.75rem", lg: "0.8rem" },
                    "& a": {
                      textDecoration: "underline",
                      color: "inherit",
                      transition: "color 0.3s ease-in-out",
                      "&:hover": { color: "yellow" },
                    },
                  }}
                >
                  <a
                    href="https://www.gad.gov.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    General Administration Department Website
                  </a>
                </Typography>
              </Box>
            )}
          </Box>

          {/* Center Section - Navigation */}
          {!isMobile && !isTablet ? (
            <Box
              sx={{
                display: "flex",
                gap: { md: 2, lg: 3 },
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {navigation.map((navItem) => {
                if (navItem.id === "subjects") {
                  return (
                    <Box
                      key={navItem.id}
                      sx={{ position: "relative" }}
                      onMouseEnter={handleSubjectsMouseEnter}
                      onMouseLeave={handleSubjectsMouseLeave}
                    >
                      <Button
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          textTransform: "none",
                          fontSize: { md: "0.9rem", lg: "1rem" },
                          padding: { md: "6px 12px", lg: "8px 16px" },
                        }}
                      >
                        {t(navItem.translationKey)}
                      </Button>
                      <Menu
                        anchorEl={subjectsAnchor}
                        open={Boolean(subjectsAnchor)}
                        onClose={() => setSubjectsAnchor(null)}
                        transitionDuration={0}
                        PaperProps={{
                          style: { overflow: "hidden" },
                          sx: {
                            maxHeight: { md: "400px", lg: "500px" },
                            width: { md: "200px", lg: "250px" },
                          },
                        }}
                        MenuListProps={{
                          onMouseEnter: () => setIsHovering(true),
                          onMouseLeave: () => setIsHovering(false),
                        }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        {sections
                          .filter((section) => section.id !== "home")
                          .map((section) => (
                            <MenuItem
                              key={section.id}
                              onClick={() => {
                                handleSectionClick(
                                  `/${section.section
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`
                                );
                                setSubjectsAnchor(null);
                              }}
                              sx={{
                                fontSize: { md: "0.875rem", lg: "1rem" },
                                padding: { md: "8px 16px", lg: "10px 20px" },
                              }}
                            >
                              {section.section}
                            </MenuItem>
                          ))}
                      </Menu>
                    </Box>
                  );
                } else {
                  return (
                    <Button
                      key={navItem.id}
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        textTransform: "none",
                        fontSize: { md: "0.9rem", lg: "1rem" },
                        padding: { md: "6px 12px", lg: "8px 16px" },
                      }}
                      onClick={() =>
                        navItem.id === "home" ? navigate("/") : undefined
                      }
                    >
                      {t(navItem.translationKey)}
                    </Button>
                  );
                }
              })}
            </Box>
          ) : (
            <IconButton
              sx={{
                color: "#fff",
                padding: { xs: "8px", sm: "12px" },
              }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon sx={{ fontSize: { xs: "24px", sm: "28px" } }} />
            </IconButton>
          )}

          {/* Right Section - Language and Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            {/* Desktop: Individual language buttons */}
            {!isMobile && (
              <>
                {["en", "hi", "mr", "gu"].map((lang) => (
                  <Button
                    key={lang}
                    variant={i18n.language === lang ? "contained" : ""}
                    sx={{
                      color: "#fff",
                      backgroundColor:
                        i18n.language === lang ? "#007bff" : "transparent",
                      padding: { sm: "4px 8px", md: "6px 10px" },
                      fontSize: { sm: "0.75rem", md: "0.8rem" },
                      minWidth: "auto",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor:
                          i18n.language === lang
                            ? "#0056b3"
                            : "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                    onClick={() => handleLanguageChange(lang)}
                  >
                    {lang === "en"
                      ? "English"
                      : lang === "hi"
                        ? "हिंदी"
                        : lang === "gu"
                          ? "ગુજરાતી"
                          : "मराठी"}
                  </Button>
                ))}
              </>
            )}

            {/* Mobile: Language selector button */}
            {isMobile && (
              <>
                <Tooltip title="Select Language" arrow>
                  <IconButton
                    onClick={handleLanguageMenuOpen}
                    sx={{
                      color: "#fff",
                      padding: { xs: "6px", sm: "8px" },
                      backgroundColor: languageMenuAnchor
                        ? "rgba(255, 255, 255, 0.1)"
                        : "transparent",
                    }}
                  >
                    <LanguageIcon
                      sx={{ fontSize: { xs: "20px", sm: "24px" } }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={languageMenuAnchor}
                  open={Boolean(languageMenuAnchor)}
                  onClose={handleLanguageMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  {["en", "hi", "mr", "gu"].map((lang) => (
                    <MenuItem
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      selected={i18n.language === lang}
                      sx={{
                        backgroundColor:
                          i18n.language === lang
                            ? "rgba(0, 123, 255, 0.1)"
                            : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(0, 123, 255, 0.15)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight:
                            i18n.language === lang ? "bold" : "normal",
                        }}
                      >
                        {getLanguageLabel(lang)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            <IconButton
              sx={{
                color: "#fff",
                padding: { xs: "6px", sm: "8px" },
              }}
            >
              <SearchIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
            </IconButton>
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={handleLogout}
                color="error"
                sx={{
                  padding: { xs: "6px", sm: "8px" },
                }}
              >
                <LogoutIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "280px", sm: "320px" },
            },
          }}
        >
          <Box
            sx={{
              padding: { xs: "16px", sm: "20px" },
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <img
              src={EmblemOfIndia}
              alt="Emblem of India"
              style={{ width: 40, height: 40 }}
            />
            <img
              src={SealOfMaharashtra}
              alt="Seal of Maharashtra"
              style={{ width: 40, height: 40 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "#061c36",
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.1rem" },
              }}
            >
              GAD
            </Typography>
          </Box>
          <List sx={{ padding: { xs: "8px", sm: "12px" } }}>
            {/* Navigation Menu Items */}
            {navigation
              .filter((navItem) => navItem.id !== "subjects")
              .map((navItem) => (
                <ListItem
                  button
                  key={navItem.id}
                  onClick={() => {
                    if (navItem.path) {
                      navigate(navItem.path);
                    } else if (navItem.id === "home") {
                      navigate("/");
                    }
                    setDrawerOpen(false);
                  }}
                  sx={{
                    borderRadius: "8px",
                    marginBottom: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(6, 28, 54, 0.08)",
                    },
                  }}
                >
                  <ListItemText
                    primary={t(navItem.translationKey)}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}

            {/* Subjects/Sections */}
            <ListItem
              sx={{
                padding: "8px 16px",
                marginTop: "8px",
                marginBottom: "4px",
              }}
              disabled
            >
              <ListItemText
                primary={t("header.subjects") || "Subjects"}
                primaryTypographyProps={{
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  fontWeight: 600,
                  color: "#666",
                  textTransform: "uppercase",
                }}
              />
            </ListItem>
            {sections
              .filter((section) => section.id !== "home")
              .map((section) => (
                <ListItem
                  button
                  key={section.id}
                  onClick={() => {
                    handleSectionClick(
                      `/${section.section.toLowerCase().replace(/\s+/g, "-")}`
                    );
                    setDrawerOpen(false);
                  }}
                  sx={{
                    borderRadius: "8px",
                    marginBottom: "4px",
                    paddingLeft: { xs: "24px", sm: "32px" },
                    "&:hover": {
                      backgroundColor: "rgba(6, 28, 54, 0.08)",
                    },
                  }}
                >
                  <ListItemText
                    primary={section.section}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}
            {error && (
              <ListItem disabled>
                <ListItemText primary={error} sx={{ color: "error.main" }} />
              </ListItem>
            )}
          </List>
          {/* Language selector in drawer for all mobile */}
          {isMobile && (
            <Box
              sx={{
                padding: "16px",
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", color: "#061c36", mb: 0.5 }}
              >
                Language
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {["en", "hi", "mr", "gu"].map((lang) => (
                  <Button
                    key={lang}
                    variant={i18n.language === lang ? "contained" : "outlined"}
                    size="small"
                    sx={{
                      color: i18n.language === lang ? "#fff" : "#061c36",
                      backgroundColor:
                        i18n.language === lang ? "#007bff" : "transparent",
                      borderColor: "#061c36",
                      fontSize: "0.75rem",
                      minWidth: "70px",
                      "&:hover": {
                        backgroundColor:
                          i18n.language === lang
                            ? "#0056b3"
                            : "rgba(6, 28, 54, 0.08)",
                        borderColor: "#061c36",
                      },
                    }}
                    onClick={() => {
                      handleLanguageChange(lang);
                      setDrawerOpen(false);
                    }}
                  >
                    {getLanguageLabel(lang)}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Drawer>
      </AppBar>
    </div>
  );
};

export default Navbar;
